import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

const MENU_HISTORY_KEY = 'help2fixMobileMenu';

const MobileHeader = ({ open, onClose }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const touchStart = useRef(null);

    const closeMenu = target => {
        const completeClose = () => {
            onClose();
            if (target && target !== location.pathname) navigate(target);
        };

        if (window.history.state?.[MENU_HISTORY_KEY]) {
            if (target) {
                window.addEventListener('popstate', completeClose, { once: true });
            }
            window.history.back();
            if (!target) onClose();
        } else {
            completeClose();
        }
    };

    useEffect(() => {
        if (!open) return undefined;

        const wrapper = document.getElementById('wrapper');
        const previousBodyOverflow = document.body.style.overflow;
        const previousHtmlOverflow = document.documentElement.style.overflow;

        if (!window.history.state?.[MENU_HISTORY_KEY]) {
            window.history.pushState({
                ...(window.history.state || {}),
                [MENU_HISTORY_KEY]: true
            }, '', window.location.href);
        }

        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
        if (wrapper) wrapper.style.transform = 'translate3d(min(80vw, 440px), 0, 0)';

        const handlePopState = () => onClose();
        const handleKeyDown = event => {
            if (event.key !== 'Escape') return;
            if (window.history.state?.[MENU_HISTORY_KEY]) window.history.back();
            else onClose();
        };

        window.addEventListener('popstate', handlePopState);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('popstate', handlePopState);
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousBodyOverflow;
            document.documentElement.style.overflow = previousHtmlOverflow;
            if (wrapper) wrapper.style.transform = 'translate3d(0, 0, 0)';
        };
    }, [open, onClose]);

    const handleTouchStart = event => {
        const touch = event.touches[0];
        touchStart.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchEnd = event => {
        if (!touchStart.current) return;
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStart.current.x;
        const deltaY = Math.abs(touch.clientY - touchStart.current.y);
        touchStart.current = null;
        if (deltaX < -55 && deltaY < 80) closeMenu();
    };

    const menuLink = path => event => {
        event.preventDefault();
        closeMenu(path);
    };

    const portal = document.getElementById('mobnav__portal');
    if (!portal) return null;

    return createPortal(<>
        {open && <div
            role="presentation"
            onClick={() => closeMenu()}
            onTouchMove={event => event.preventDefault()}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9998,
                background: 'rgba(0, 0, 0, 0.55)',
                touchAction: 'none'
            }} />}

        <nav
            role="dialog"
            aria-modal="true"
            aria-hidden={!open}
            aria-label={t('Menu')}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            className="mmenu-init mm-menu mm-offcanvas mm-opened"
            id="mm-0"
            style={{
                position: 'fixed',
                zIndex: 9999,
                top: 0,
                left: 0,
                width: '80vw',
                maxWidth: '440px',
                minWidth: '140px',
                height: '100dvh',
                transform: open ? 'translateX(0)' : 'translateX(-100%)',
                transition: 'transform 0.3s ease, visibility 0.3s',
                visibility: open ? 'visible' : 'hidden',
                backgroundColor: '#2a2a2a',
                touchAction: 'pan-y'
            }}>
            <div className="mm-panels">
                <div className="mm-panel mm-hasnavbar mm-opened" id="mm-1">
                    <div style={{ transition: '0.3s', position: 'relative' }} className="mm-navbar">
                        <span className="mm-title">{t('Menu')}</span>
                        <button
                            type="button"
                            aria-label={t('CloseMenu')}
                            onClick={() => closeMenu()}
                            style={{
                                position: 'absolute',
                                right: '14px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                padding: '8px 12px',
                                border: 0,
                                background: 'transparent',
                                color: '#fff',
                                fontSize: '30px',
                                lineHeight: 1,
                                cursor: 'pointer'
                            }}>
                            ×
                        </button>
                    </div>
                    <ul className="mm-listview">
                        <li>
                            <NavLink to="/" end onClick={menuLink('/')}
                                className={({ isActive }) => isActive ? 'current' : ''}>
                                {t('Home')}
                            </NavLink>
                        </li>
                        <li>
                            <NavLink to="/faqs" end onClick={menuLink('/faqs')}
                                className={({ isActive }) => isActive ? 'current' : ''}>FAQs</NavLink>
                        </li>
                        <li>
                            <NavLink to="/contact" end onClick={menuLink('/contact')}
                                className={({ isActive }) => isActive ? 'current' : ''}>{t('ContactUs')}</NavLink>
                        </li>
                    </ul>
                </div>
            </div>
        </nav>
    </>, portal);
};

export default MobileHeader;
